import { fetchNotificationMessageById, fetchNotifications } from '@/features/server-notifications/api';
import { Env } from '@/shared/config/environment';
import { LocalStore } from '@/shared/lib/local-storage';

const state = {
  showOnlyUnreadMessages: new LocalStore('notifications_').get('unread', false),
  notificationsList: [],
  isLoadingNotifications: false,
  notificationsError: false,
  meta: {
    currentPage: 0,
    totalPages: 0,
  },
};

const mutations = {
  SET_NOTIFICATIONS_LIST(state, notificationsList) {
    if (state.notificationsList?.length) {
      const existingIds = new Set(state.notificationsList.map(notif => notif.id));
      const uniqueNotifications = notificationsList.filter(
        notif => !existingIds.has(notif.id)
      );

      if (uniqueNotifications.length) {
        state.notificationsList.push(...uniqueNotifications);
      }
    } else {
      state.notificationsList = notificationsList;
    }
  },
  SET_NOTIFICATIONS_LIST_META(state, meta) {
    state.meta = meta;
  },
  SET_LOADING_NOTIFICATIONS(state, isLoading) {
    state.isLoadingNotifications = isLoading;
  },
  SET_NEW_NOTIFICATION(state, notification) {
    if (!notification || !notification.id) {
      return;
    }

    const newList = state.notificationsList ? [...state.notificationsList] : [];
    const existingIndex = newList.findIndex(n => n.id === notification.id);

    if (existingIndex === -1) {
      state.notificationsList = [{ id: notification.id, read: false, title: notification.title, message: notification.message, created_at: notification.created_at }, ...newList];
    }
  },
  UPDATE_NOTIFICATION(state, updatedNotification) {
    if (state.notificationsList?.length) {
      const index = state.notificationsList.findIndex(n => n.id === updatedNotification.id);
      if (index !== -1) {
        state.notificationsList = [
          ...state.notificationsList.slice(0, index),
          updatedNotification,
          ...state.notificationsList.slice(index + 1)
        ];
      }
    }
  },
  SET_NOTIFICATIONS_ERROR(state, error) {
    state.notificationsError = error;
  },
  SET_DISPLAY_ONLY_UNREAD_MESSAGES(state, unreadOnly) {
    state.showOnlyUnreadMessages = unreadOnly;
    new LocalStore('notifications_').set('unread', unreadOnly);
  },
};

const actions = {
  async fetchNotificationsList({ commit, dispatch }, page = 1, onlyUnreadMessages = false) {
    commit('SET_LOADING_NOTIFICATIONS', true);
    commit('SET_NOTIFICATIONS_ERROR', null);

    try {
      const notifications = await fetchNotifications({ page: page, onlyUnreadMessages: onlyUnreadMessages });
      commit('SET_NOTIFICATIONS_LIST', notifications?.data);
      commit('SET_NOTIFICATIONS_LIST_META', { currentPage: page, totalPages: notifications?.meta?.last_page });
    } catch (error) {
      const errorMessage = error.response?.data?.message
            || error.response?.data?.error
            || error.message
            || 'Не вдалося завантажити сповіщення';

      commit('SET_NOTIFICATIONS_ERROR', errorMessage);
      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося завантажити сповіщення',
          message: errorMessage,
        }, { root: true });
      }

      throw error;
    } finally {
      commit('SET_LOADING_NOTIFICATIONS', false);
    }
  },
  async getNotificationMessage({ commit, dispatch }, messageId) {
    try {
      const notification = await fetchNotificationMessageById(messageId);
      if (state.notificationsList?.some(n => n.id === messageId)) {
        commit('UPDATE_NOTIFICATION', notification);
      }

      return notification;
    } catch (error) {
      const errorMessage = error.response?.data?.message
              || error.response?.data?.error
              || error.message
              || 'Не вдалося завантажити сповіщення';

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося завантажити сповіщення',
          message: errorMessage,
        }, { root: true });
      }

      throw error;
    }
  },
  setShowOnlyUnreadMessages({ commit }, unreadOnly) {
    commit('SET_DISPLAY_ONLY_UNREAD_MESSAGES', unreadOnly);
  },
  setNewNotification({ commit }, notification) {
    commit('SET_NEW_NOTIFICATION', notification);
  },
  clearNotifications({ commit }) {
    commit('SET_NOTIFICATIONS_LIST', null);
    commit('SET_NOTIFICATIONS_LIST_META', { page: 0, totalPages: 0 });
  },
};

const getters = {
  isLoadingNotification: (state) => state.isLoadingNotifications,
  notificationsError: (state) => state.notificationsError,
  isNotificationsDataReady: (state) => state.notificationsList !== null && state.notificationsList?.length >= 0,
  isEmpty: (state) => state.notificationsList?.length === 0,

  getShowOnlyUnreadMessages: () => state.showOnlyUnreadMessages,
  getNotifications: (state) => state.notificationsList,
  hasMoreData: (state) => (state.isLoadingNotifications === false && state.notificationsList !== null) && state.meta.currentPage < state.meta.totalPages,
  getLastUnreadNotification: (state) => state.notificationsList?.reduce((latest, message) => {
    return (!message.read && (!latest || message.created_at > latest.created_at)) ? message : latest;
  }, null),
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
